import { Component, inject, input } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { RIGHT_PANEL_CONFIG } from "@bitwarden/web-vault/app/models/constants/global.constants";
import {
  GLASS_BUTTON_BG,
  GLASS_BUTTON_SHADOW,
} from "@bitwarden/web-vault/app/models/constants/glass-button.constants";

export interface CreditsAddedReceiptRow {
  label: string;
  value: string;
  /** Trailing unit (CrG/CrP/USD/AUD), rendered in its own Caption/Small style rather than
   *  baked into `value` — omitted for rows with no unit (e.g. Transaction ID). */
  unit?: string;
  highlight?: boolean;
}

export function formatCreditsAmount(value: number): string {
  return new Intl.NumberFormat().format(value);
}

/** Decorative teal swoosh behind the receipt, matching the accent tones used elsewhere
 *  (see youGetGoldBg/GLASS_BUTTON_BG) — bottom-right radial fade over the panel's surface color. */
const CREDITS_ADDED_BG =
  "radial-gradient(140% 90% at 100% 100%, rgba(0, 196, 196, 0.16) 0%, rgba(0, 178, 169, 0.05) 45%, rgba(0, 178, 169, 0) 70%), var(--color-surface-card)";

/** Shared by both entry points (PointsPurchaseComponent's convert/Apple-Pay success, and
 *  CreditsComponent's Stripe-return handling) so the dialog is always opened identically. */
export function openCreditsAddedDialog(
  dialog: MatDialog,
  goldAdded: number | null,
  subtitle: string,
  rows: CreditsAddedReceiptRow[],
): void {
  dialog.open(DialogWrapperComponent, {
    ...RIGHT_PANEL_CONFIG,
    data: {
      title: "creditsAdded",
      showBackButton: false,
      bodyBackground: CREDITS_ADDED_BG,
      component: CreditsAddedComponent,
      componentInputs: { goldAdded, subtitle, rows },
    },
  });
}

/** The "Gold credits added" row both receipts start with — dropped when goldAdded is null. */
function goldAddedRows(i18n: I18nService, goldAdded: number | null): CreditsAddedReceiptRow[] {
  return goldAdded === null
    ? []
    : [
        {
          label: i18n.t("goldCreditsAdded"),
          value: `+${formatCreditsAmount(goldAdded)}`,
          unit: "CrG",
        },
      ];
}

/** Builds the receipt for a Platinum → Gold conversion (screen: "Credits added · Convert").
 *  goldAdded is null when the (committed) conversion's response didn't say how much Gold landed;
 *  its row is dropped rather than guessed, same as buildCashReceipt. */
export function buildConvertReceipt(
  i18n: I18nService,
  args: {
    goldAdded: number | null;
    platinumUsed: number;
    newGoldBalance: number;
    transactionId: string;
  },
): { subtitle: string; rows: CreditsAddedReceiptRow[] } {
  return {
    subtitle: i18n.t("convertedInstantSubtitle", formatCreditsAmount(args.platinumUsed)),
    rows: [
      ...goldAddedRows(i18n, args.goldAdded),
      {
        label: i18n.t("platinumUsed"),
        value: `-${formatCreditsAmount(args.platinumUsed)}`,
        unit: "CrP",
      },
      {
        label: i18n.t("newGoldBalance"),
        value: formatCreditsAmount(args.newGoldBalance),
        unit: "CrG",
        highlight: true,
      },
      { label: i18n.t("transactionId"), value: args.transactionId },
    ],
  };
}

/**
 * Builds the receipt for a cash purchase (screen: "Credits added · Buy with cash"). The paid
 * amount only surfaces in the subtitle ("Paid $X · receipt sent to ...") — there is no separate
 * "Paid" row. paidAmount/paidCurrency are omitted when not available (e.g. a cross-device Stripe
 * return that never had the pack stashed locally) — the subtitle falls back to the plain
 * "Credits added" copy rather than showing a fabricated amount. goldAdded is null for the same
 * reason when the backend's detail omits `credits` too; its row is dropped rather than showing +0.
 */
export function buildCashReceipt(
  i18n: I18nService,
  args: {
    goldAdded: number | null;
    newGoldBalance: number;
    paidAmount?: number;
    paidCurrency?: string;
    transactionId: string;
    email?: string;
  },
): { subtitle: string; rows: CreditsAddedReceiptRow[] } {
  const rows = goldAddedRows(i18n, args.goldAdded);

  let subtitle: string;
  if (typeof args.paidAmount === "number" && args.paidCurrency) {
    const paidAmount = args.paidAmount.toFixed(2);
    subtitle = args.email
      ? i18n.t("cashReceiptSubtitle", paidAmount, args.paidCurrency, args.email)
      : i18n.t("cashReceiptSubtitleNoEmail", paidAmount, args.paidCurrency);
  } else {
    subtitle = i18n.t("creditsAdded");
  }

  rows.push(
    {
      label: i18n.t("newGoldBalance"),
      value: formatCreditsAmount(args.newGoldBalance),
      unit: "CrG",
      highlight: true,
    },
    { label: i18n.t("transactionId"), value: args.transactionId },
  );

  return { subtitle, rows };
}

/**
 * Success/receipt screen shown after a Gold-credit top-up completes — either by converting
 * Platinum credits (PointsPurchaseComponent's "Use Platinum Credits" tab) or by a cash purchase
 * (Stripe/Apple Pay, "Buy with Cash" tab). Always opened as its own DialogWrapperComponent
 * instance (title "creditsAdded") rather than swapped in place, since DialogWrapperComponent
 * reads its title once from MAT_DIALOG_DATA and isn't reactive to a child changing screens.
 */
@Component({
  selector: "app-credits-added",
  templateUrl: "./credits-added.component.html",
})
export class CreditsAddedComponent {
  private dialogRef = inject<MatDialogRef<unknown>>(MatDialogRef, { optional: true });

  /** Headline number, e.g. 250 or 25000. null when it isn't known (see buildCashReceipt) — the
   *  headline then drops the number instead of claiming 0. */
  goldAdded = input.required<number | null>();
  /** Subtitle under the headline — variant-specific copy composed by buildConvertReceipt/buildCashReceipt. */
  subtitle = input.required<string>();
  /** Ordered receipt line items (label/value pairs); last row is typically the transaction id. */
  rows = input.required<CreditsAddedReceiptRow[]>();

  protected readonly glassButtonBg = GLASS_BUTTON_BG;
  protected readonly glassButtonShadow = GLASS_BUTTON_SHADOW;

  protected done(): void {
    this.dialogRef?.close();
  }
}
